import { telemetry } from "@/utilities/telemetry";
import { isDuplicateBetslip } from "@/features/betslip/utils/rules";
import {
  buildSubmissionSnapshot,
  readLastSubmitted,
} from "@/features/betslip/services/submitBet/persistence";
import type {
  BetslipStoreHandle,
  SubmitBetParams,
} from "@/features/betslip/services/submitBet/types";

export type PreflightResult =
  | {
      kind: "proceed";
      stakedSingles: Array<[string, number]>;
      hasMultiStake: boolean;
    }
  | { kind: "skip" }
  | { kind: "duplicate" };

export const checkPreflight = (
  store: ReturnType<BetslipStoreHandle["getState"]>,
  params: SubmitBetParams,
): PreflightResult => {
  if (store.submission.phase === "submitting") return { kind: "skip" };

  const stakedSingles = Object.entries(store.stakes.singles).filter(
    ([, amt]) => amt > 0,
  );
  const hasMultiStake = store.stakes.multi > 0 && store.multiLegs.length >= 2;

  if (!params.acceptDuplicate) {
    const last = readLastSubmitted(params.userId);
    if (last?.outcomeIds?.length && isDuplicate(store, last)) {
      telemetry.duplicateBetAttempt({
        betType: hasMultiStake ? "Multi" : "Single",
        betslipMessage: "",
        betslipUIState: "duplicate",
        canUsePromoMulti: false,
        isMultiUsingPromo: store.promoCash.multi,
        messageId: null,
        multiAccepted: null,
        multiStake: store.stakes.multi,
        oddsFormat: "",
        stakeAll: 0,
      });
      return { kind: "duplicate" };
    }
  }

  return { kind: "proceed", stakedSingles, hasMultiStake };
};

/**
 * A repeat submit is only a duplicate when the selections and every stake
 * behind them are unchanged. Restaking makes it a different bet.
 */
const isDuplicate = (
  store: ReturnType<BetslipStoreHandle["getState"]>,
  last: NonNullable<ReturnType<typeof readLastSubmitted>>,
): boolean => {
  const current = buildSubmissionSnapshot(store);

  if (!isDuplicateBetslip(current.outcomeIds, last.outcomeIds)) return false;

  if (last.multiStake !== current.multiStake) return false;

  // Snapshots written before single stakes were tracked can't answer this,
  // so they fall back to matching on the selections and the other stakes.
  if (
    last.singleStakes &&
    !sameNumberMap(last.singleStakes, current.singleStakes ?? {})
  ) {
    return false;
  }

  // Like singleStakes: an older snapshot matches on what it does carry.
  if (!last.combinationStakes) return true;

  return sameNumberMap(last.combinationStakes, current.combinationStakes ?? {});
};

const sameNumberMap = (
  a: Record<string, number>,
  b: Record<string, number>,
): boolean => {
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) return false;
  for (const key of keys) {
    if (a[key] !== b[key]) return false;
  }
  return true;
};
